import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';

function DivePlanning() {
  const planningTools = [
    {
      icon: 'fas fa-calendar-alt',
      title: 'Multi-Day Trip Planner',
      description: 'Plan entire dive trips with day-by-day scheduling, surface intervals, and nitrogen loading estimation.',
      features: ['Trip duration scheduling', 'Surface interval calculator', 'Dive sequence optimization'],
      gradient: 'linear-gradient(135deg, rgba(102, 126, 234, 0.8) 0%, rgba(118, 75, 162, 0.75) 100%)'
    },
    {
      icon: 'fas fa-toolbox',
      title: 'Equipment Packing List',
      description: 'Professional equipment management with pre-built templates for different diving conditions.',
      features: ['11 equipment categories', '100+ pre-loaded items', 'Custom list creation'],
      gradient: 'linear-gradient(135deg, rgba(240, 147, 251, 0.8) 0%, rgba(245, 87, 108, 0.75) 100%)'
    },
    {
      icon: 'fas fa-flask',
      title: 'Gas Planning Calculator',
      description: 'Comprehensive gas planning suite for recreational and technical diving.',
      features: ['SAC rate calculation', 'Nitrox & Trimix planning', 'Best mix recommendations'],
      gradient: 'linear-gradient(135deg, rgba(79, 172, 254, 0.8) 0%, rgba(0, 242, 254, 0.7) 100%)'
    },
    {
      icon: 'fas fa-weight',
      title: 'Buoyancy Calculator',
      description: 'Calculate perfect weighting for any dive configuration and conditions.',
      features: ['Equipment weight factors', 'Salinity adjustments', 'Experience-based guidelines'],
      gradient: 'linear-gradient(135deg, rgba(67, 233, 123, 0.8) 0%, rgba(56, 249, 215, 0.7) 100%)'
    },
    {
      icon: 'fas fa-stopwatch',
      title: 'No-Deco Planner',
      description: 'Plan no-decompression dives with NDL optimization and safety margins.',
      features: ['Multi-level planning', 'Safety stop calculation', 'Altitude adjustments'],
      gradient: 'linear-gradient(135deg, rgba(250, 112, 154, 0.8) 0%, rgba(254, 225, 64, 0.7) 100%)'
    },
    {
      icon: 'fas fa-table',
      title: 'Decompression Tables',
      description: 'Visual decompression tables with PADI RDP and Bühlmann algorithm support.',
      features: ['Interactive tables', 'Pressure group tracking', 'Repetitive dive planning'],
      gradient: 'linear-gradient(135deg, rgba(48, 207, 208, 0.8) 0%, rgba(51, 8, 103, 0.75) 100%)'
    }
  ];

  return (
    <section id="planning" className="section-padding">
      <Container>
        <div className="text-center mb-5" data-aos="fade-up">
          <h2 className="section-title">Professional Dive Planning</h2>
          <p className="section-subtitle">
            Comprehensive tools for planning safe and successful dives
          </p>
        </div>

        <Row>
          {planningTools.map((tool, index) => (
            <Col lg={4} md={6} className="mb-4" key={index} data-aos="fade-up" data-aos-delay={index * 100}>
              <Card className="planning-card">
                <div style={{
                  background: tool.gradient,
                  padding: '2rem',
                  color: 'white'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'rgba(255,255,255,0.22)',
                    border: '1px solid rgba(255,255,255,0.35)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4)',
                    borderRadius: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1rem'
                  }}>
                    <i className={tool.icon} style={{ fontSize: '1.8rem' }}></i>
                  </div>
                  <h4 style={{ marginBottom: '1rem' }}>{tool.title}</h4>
                  <p style={{ opacity: 0.95, marginBottom: 0 }}>{tool.description}</p>
                </div>
                <Card.Body style={{ background: 'transparent', color: 'var(--text-primary)' }}>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {tool.features.map((feature, idx) => (
                      <li key={idx} style={{ 
                        padding: '0.5rem 0',
                        borderBottom: idx < tool.features.length - 1 ? '1px solid var(--border-color)' : 'none'
                      }}>
                        <i className="fas fa-check" style={{ 
                          color: '#4CAF50',
                          marginRight: '10px'
                        }}></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <div className="text-center mt-5" data-aos="zoom-in">
          <div className="calc-panel">
            <h3 className="mb-3">
              <i className="fas fa-calculator" style={{ marginRight: '10px' }}></i>
              Advanced Calculations
            </h3>
            <p style={{ fontSize: '1.1rem', marginBottom: '2rem', opacity: 0.95 }}>
              All planning tools use professional-grade algorithms and calculations based on 
              established diving science, including PADI RDP tables, Bühlmann decompression 
              models, and industry-standard gas planning formulas.
            </p>
            <Row>
              <Col md={4}>
                <div style={{ padding: '1rem' }}>
                  <i className="fas fa-brain" style={{ fontSize: '2.5rem', marginBottom: '1rem' }}></i>
                  <h5>Smart Recommendations</h5>
                  <p style={{ fontSize: '0.9rem', opacity: 0.9 }}>
                    AI-driven suggestions based on your diving history
                  </p>
                </div>
              </Col>
              <Col md={4}>
                <div style={{ padding: '1rem' }}>
                  <i className="fas fa-shield-alt" style={{ fontSize: '2.5rem', marginBottom: '1rem' }}></i>
                  <h5>Safety Margins</h5>
                  <p style={{ fontSize: '0.9rem', opacity: 0.9 }}>
                    Conservative calculations with adjustable safety factors
                  </p>
                </div>
              </Col>
              <Col md={4}>
                <div style={{ padding: '1rem' }}>
                  <i className="fas fa-save" style={{ fontSize: '2.5rem', marginBottom: '1rem' }}></i>
                  <h5>Save & Reuse</h5>
                  <p style={{ fontSize: '0.9rem', opacity: 0.9 }}>
                    Save your plans and templates for future dives
                  </p>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </Container>
    </section>
  );
}

export default DivePlanning;